import { Suspense, useEffect } from 'react'
import { lazyWithRetry } from './utils/lazyWithRetry'
import { useRouteDocumentTitle } from './hooks/useDocumentTitle'
import { Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom'
import ProtectedRoute from './components/ProtectedRoute'
import SubscriptionGuard from './components/SubscriptionGuard'
import ClientProtectedRoute from './components/ClientProtectedRoute'
import AppLayout from './components/AppLayout'
import ClientPortalLayout from './components/ClientPortalLayout'
import { SessionProvider } from './hooks/useSession'
import { WorkViewProvider } from './hooks/useWorkView'
import { ClientSessionProvider } from './hooks/useClientSession'
import { QueryProvider } from './providers/QueryProvider'
import { ToastContainer } from './components/Toast'
import { ErrorBoundary } from './components/ErrorBoundary'
import UpdatePrompt from './components/UpdatePrompt'
import { RouteErrorBoundary } from './components/RouteErrorBoundary'
import CookieConsent from './components/CookieConsent'
import { trackPageView } from './utils/analytics'

// Critical path - keep in main bundle
import Landing from './pages/Landing'
import Dashboard from './pages/Dashboard'
import Login from './pages/Login'
import Register from './pages/Register'
import Onboarding from './pages/Onboarding'
import NotFound from './pages/NotFound'

// Lazy load non-critical routes for better initial load performance
const ForgotPassword = lazyWithRetry(() => import('./pages/ForgotPassword'))
const ResetPassword = lazyWithRetry(() => import('./pages/ResetPassword'))
const VerifyEmail = lazyWithRetry(() => import('./pages/VerifyEmail'))
const PaymentSuccess = lazyWithRetry(() => import('./pages/PaymentSuccess'))
const Terms = lazyWithRetry(() => import('./pages/Terms'))
const Privacy = lazyWithRetry(() => import('./pages/Privacy'))
const Pricing = lazyWithRetry(() => import('./pages/Pricing'))
/** The page an agent sends to a recruit's family. */
const ForFamilies = lazyWithRetry(() => import('./pages/ForFamilies'))
/**
 * The screenshot harness. The import sits inside the DEV branch so a
 * production build drops it entirely rather than shipping a chunk full of
 * sample athletes that nothing can route to.
 */
const Shots = import.meta.env.DEV ? lazyWithRetry(() => import('./dev/Shots')) : null

// Clients
const TheBook = lazyWithRetry(() => import('./pages/TheBook'))
const ClientDetail = lazyWithRetry(() => import('./pages/ClientDetail'))
const ClientForm = lazyWithRetry(() => import('./pages/ClientForm'))
const EarningsPrintView = lazyWithRetry(() => import('./pages/client-detail/EarningsPrintView'))

// Prospects
const Prospects = lazyWithRetry(() => import('./pages/Prospects'))
const ProspectDetail = lazyWithRetry(() => import('./pages/ProspectDetail'))
const ProspectForm = lazyWithRetry(() => import('./pages/ProspectForm'))

// Deals
const Pipeline = lazyWithRetry(() => import('./pages/Pipeline'))
const DealForm = lazyWithRetry(() => import('./pages/DealForm'))
const DealDetail = lazyWithRetry(() => import('./pages/DealDetail'))
const DealTemplates = lazyWithRetry(() => import('./pages/DealTemplates'))
const DealComparison = lazyWithRetry(() => import('./pages/DealComparison'))
const WinLossReport = lazyWithRetry(() => import('./pages/WinLossReport'))

// Player Transactions (movements, signings, trades)
const PlayerTransactions = lazyWithRetry(() => import('./pages/PlayerTransactions'))
const PlayerTransactionForm = lazyWithRetry(() => import('./pages/PlayerTransactionForm'))

// Compliance
const ComplianceRegister = lazyWithRetry(() => import('./pages/ComplianceRegister'))
const ComplianceDetail = lazyWithRetry(() => import('./pages/ComplianceDetail'))
const ComplianceForm = lazyWithRetry(() => import('./pages/ComplianceForm'))
const ComplianceTemplates = lazyWithRetry(() => import('./pages/ComplianceTemplates'))
const ComplianceCalendar = lazyWithRetry(() => import('./pages/ComplianceCalendar'))

// Organizations & Contacts
const OrganizationsRegister = lazyWithRetry(() => import('./pages/OrganizationsRegister'))
const OrganizationDetail = lazyWithRetry(() => import('./pages/OrganizationDetail'))
const OrganizationForm = lazyWithRetry(() => import('./pages/OrganizationForm'))
const ContactsRegister = lazyWithRetry(() => import('./pages/ContactsRegister'))
const ContactDetail = lazyWithRetry(() => import('./pages/ContactDetail'))
const ContactForm = lazyWithRetry(() => import('./pages/ContactForm'))

// Calendar & Tasks
const Calendar = lazyWithRetry(() => import('./components/calendar'))
const CalendarEventForm = lazyWithRetry(() => import('./pages/CalendarEventForm'))
const Tasks = lazyWithRetry(() => import('./pages/Tasks'))
const Vendors = lazyWithRetry(() => import('./pages/Vendors'))
const TaskForm = lazyWithRetry(() => import('./pages/TaskForm'))

// Contracts & Financial
const ContractRegister = lazyWithRetry(() => import('./pages/ContractRegister'))
const ContractDetail = lazyWithRetry(() => import('./pages/ContractDetail'))
const ContractForm = lazyWithRetry(() => import('./pages/ContractForm'))
const Payments = lazyWithRetry(() => import('./pages/Payments'))
const Invoices = lazyWithRetry(() => import('./pages/Invoices'))
const InvoiceDetail = lazyWithRetry(() => import('./pages/InvoiceDetail'))
const InvoiceForm = lazyWithRetry(() => import('./pages/InvoiceForm'))
const Transactions = lazyWithRetry(() => import('./pages/Transactions'))
const TransactionDetail = lazyWithRetry(() => import('./pages/TransactionDetail'))
const TransactionForm = lazyWithRetry(() => import('./pages/TransactionForm'))

// Analytics & Reports
const Analytics = lazyWithRetry(() => import('./pages/Analytics'))
const IncentiveDashboard = lazyWithRetry(() => import('./pages/IncentiveDashboard'))
const CommissionDashboard = lazyWithRetry(() => import('./pages/CommissionDashboard'))
const RevenueFees = lazyWithRetry(() => import('./pages/RevenueFees'))
const NeedsYou = lazyWithRetry(() => import('./pages/NeedsYou'))
const Outreach = lazyWithRetry(() => import('./pages/Outreach'))
const PortalAccess = lazyWithRetry(() => import('./pages/PortalAccess'))
const Audience = lazyWithRetry(() => import('./pages/Audience'))
const Reports = lazyWithRetry(() => import('./pages/Reports'))
const ReportBuilder = lazyWithRetry(() => import('./pages/ReportBuilder'))
const ClientPortfolioReport = lazyWithRetry(() => import('./pages/ClientPortfolioReport'))
const SharedReportView = lazyWithRetry(() => import('./pages/SharedReportView'))
const ScoutPackageView = lazyWithRetry(() => import('./pages/ScoutPackageView'))
const PartnerPageView = lazyWithRetry(() => import('./pages/PartnerPageView'))
const ConnectSocial = lazyWithRetry(() => import('./pages/ConnectSocial'))
const PlayerPackageBuilder = lazyWithRetry(() => import('./pages/PlayerPackageBuilder'))

const Activities = lazyWithRetry(() => import('./pages/Activities'))
const Signals = lazyWithRetry(() => import('./pages/Signals'))

// Search
const SearchResults = lazyWithRetry(() => import('./pages/SearchResults'))

// Admin & Settings
const Settings = lazyWithRetry(() => import('./pages/Settings'))
const SubscriptionRequired = lazyWithRetry(() => import('./pages/SubscriptionRequired'))
const AdminPanel = lazyWithRetry(() => import('./pages/AdminPanel'))
const PendingCustomers = lazyWithRetry(() => import('./pages/PendingCustomers'))
const AgencySetup = lazyWithRetry(() => import('./pages/AgencySetup'))
const DataHealth = lazyWithRetry(() => import('./pages/DataHealth'))
const TagManager = lazyWithRetry(() => import('./pages/TagManager'))
const Goals = lazyWithRetry(() => import('./pages/Goals'))
const Relationships = lazyWithRetry(() => import('./pages/Relationships'))
const TeamManagement = lazyWithRetry(() => import('./pages/TeamManagement'))
const ClientInbox = lazyWithRetry(() => import('./pages/ClientInbox'))
const Help = lazyWithRetry(() => import('./pages/Help'))
const NotificationCenter = lazyWithRetry(() => import('./components/NotificationCenter'))

// Client Portal
const PortalLogin = lazyWithRetry(() => import('./pages/portal/PortalLogin'))
const PortalVerify = lazyWithRetry(() => import('./pages/portal/PortalVerify'))
/** Where a texted sign-in link lands. */
const PortalStart = lazyWithRetry(() => import('./pages/portal/PortalStart'))
const PortalDeals = lazyWithRetry(() => import('./pages/portal/PortalDeals'))
const PortalDealDetail = lazyWithRetry(() => import('./pages/portal/PortalDealDetail'))
const PortalMessages = lazyWithRetry(() => import('./pages/portal/PortalMessages'))
const PortalDocuments = lazyWithRetry(() => import('./pages/portal/PortalDocuments'))
const PortalContracts = lazyWithRetry(() => import('./pages/portal/PortalContracts'))
const PortalContractDetail = lazyWithRetry(() => import('./pages/portal/PortalContractDetail'))
const PortalIncentives = lazyWithRetry(() => import('./pages/portal/PortalIncentives'))
const PortalFinancialDashboard = lazyWithRetry(() => import('./pages/portal/PortalFinancialDashboard'))
const PortalActionItems = lazyWithRetry(() => import('./pages/portal/PortalActionItems'))
const PortalCompliance = lazyWithRetry(() => import('./pages/portal/PortalCompliance'))
const PortalVendors = lazyWithRetry(() => import('./pages/portal/PortalVendors'))
const PortalSchedule = lazyWithRetry(() => import('./pages/portal/PortalSchedule'))
const PortalProfile = lazyWithRetry(() => import('./pages/portal/PortalProfile'))
// Loading fallback for lazy loaded components
function PageLoader() {
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100%',
      minHeight: '200px',
    }}>
      <div style={{
        width: '32px',
        height: '32px',
        border: '3px solid rgba(255,255,255,0.1)',
        borderTopColor: '#C8A45C',
        borderRadius: 0,
        animation: 'spin 1s linear infinite',
      }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  )
}

function PageViewTracker() {
  const location = useLocation()
  useEffect(() => {
    trackPageView(location.pathname)
  }, [location.pathname])
  return null
}

/** Keeps the browser tab named after the page. See utils/documentTitle. */
function DocumentTitle() {
  useRouteDocumentTitle()
  return null
}

/** Wraps child routes in a RouteErrorBoundary so crashes are isolated.
 *  key={pathname} forces the boundary to remount (and reset) on every navigation,
 *  so a crash on one page never bleeds into the next. */
function RouteGroup({ name }: { name: string }) {
  const { pathname } = useLocation()
  return (
    <RouteErrorBoundary key={pathname} groupName={name}>
      <Outlet />
    </RouteErrorBoundary>
  )
}

export default function App() {
  return (
    <ErrorBoundary>
      <QueryProvider>
        <SessionProvider>
          <WorkViewProvider>
          <ToastContainer />
          <UpdatePrompt />
          <PageViewTracker />
          <DocumentTitle />
          <Suspense fallback={<PageLoader />}>
            <Routes>
              {/* Public routes */}
              <Route path="/" element={<Landing />} />
              <Route path="/athletes" element={<ForFamilies />} />
              {/* The screenshot harness: dev server only, never in a build. */}
              {Shots && <Route path="/__shots/:shot" element={<Shots />} />}
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/payment-success" element={<PaymentSuccess />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/verify-email" element={<VerifyEmail />} />
              <Route path="/auth" element={<Navigate to="/login" replace />} />
              <Route path="/report/:shareToken" element={<SharedReportView />} />
              <Route path="/scout/package/:shareToken" element={<ScoutPackageView />} />
              <Route path="/partners/:token" element={<PartnerPageView />} />
              <Route path="/connect/:token" element={<ConnectSocial />} />

              {/* Onboarding - protected but no layout */}
              <Route element={<ProtectedRoute />}>
                <Route path="/onboarding" element={<Onboarding />} />
                {/* Subscription required — authenticated but no AppLayout */}
                <Route path="/subscription-required" element={<SubscriptionRequired />} />
              </Route>

              {/* Protected routes with app layout */}
              <Route element={<ProtectedRoute requireAgency />}>
                <Route element={<SubscriptionGuard />}>
                <Route element={<AppLayout />}>
                  {/* Dashboard */}
                  <Route element={<RouteGroup name="Dashboard" />}>
                    <Route path="/dashboard" element={<Dashboard />} />
                    <Route path="/signals" element={<Signals />} />
                  </Route>

                  {/* Analytics & Intelligence */}
                  <Route element={<RouteGroup name="Analytics" />}>
                    <Route path="/analytics" element={<Analytics />} />
                    <Route path="/advanced-analytics" element={<Navigate to="/analytics?tab=deals" replace />} />
                    <Route path="/portfolio-analytics" element={<Navigate to="/analytics?tab=portfolio" replace />} />
                    <Route path="/incentives" element={<IncentiveDashboard />} />
                    <Route path="/commissions" element={<CommissionDashboard />} />
                    <Route path="/revenue" element={<RevenueFees />} />
                    <Route path="/needs-you" element={<NeedsYou />} />
                    <Route path="/outreach" element={<Outreach />} />
                    <Route path="/portal-access" element={<PortalAccess />} />
                    <Route path="/audience" element={<Audience />} />
                  </Route>
{/* Clients */}
                  <Route element={<RouteGroup name="Clients" />}>
                    <Route path="clients">
                      <Route index element={<TheBook />} />
                      <Route path="new" element={<ClientForm />} />
                      <Route path=":clientId" element={<ClientDetail />} />
                      <Route path=":clientId/edit" element={<ClientForm />} />
                      <Route path=":clientId/earnings/print" element={<EarningsPrintView />} />
                    </Route>
                  </Route>
                  {/* Prospects */}
                  <Route element={<RouteGroup name="Prospects" />}>
                    <Route path="prospects">
                      <Route index element={<Prospects />} />
                      <Route path="new" element={<ProspectForm />} />
                      <Route path=":prospectId" element={<ProspectDetail />} />
                      <Route path=":prospectId/edit" element={<ProspectForm />} />
                    </Route>
                  </Route>
                  {/* Deals */}
                  <Route element={<RouteGroup name="Deals" />}>
                    <Route path="deals">
                      <Route index element={<Pipeline />} />
                      <Route path="pipeline" element={<Navigate to="/deals" replace />} />
                      <Route path="templates" element={<DealTemplates />} />
                      <Route path="compare" element={<DealComparison />} />
                      <Route path="win-loss" element={<WinLossReport />} />
                      <Route path="new" element={<DealForm />} />
                      <Route path=":dealId" element={<DealDetail />} />
                      <Route path=":dealId/edit" element={<DealForm />} />
                    </Route>
                  </Route>

                  {/* Player Transactions */}
                  <Route element={<RouteGroup name="Player Transactions" />}>
                    <Route path="player-transactions">
                      <Route index element={<PlayerTransactions />} />
                      <Route path="new" element={<PlayerTransactionForm />} />
                      <Route path=":transactionId/edit" element={<PlayerTransactionForm />} />
                    </Route>
                  </Route>
                  {/* Compliance */}
                  <Route element={<RouteGroup name="Compliance" />}>
                    <Route path="compliance">
                      <Route index element={<ComplianceRegister />} />
                      <Route path="templates" element={<ComplianceTemplates />} />
                      <Route path="calendar" element={<ComplianceCalendar />} />
                      <Route path="new" element={<ComplianceForm />} />
                      <Route path=":complianceId" element={<ComplianceDetail />} />
                      <Route path=":complianceId/edit" element={<ComplianceForm />} />
                    </Route>
                  </Route>
                  {/* Organizations & Contacts */}
                  <Route element={<RouteGroup name="Contacts" />}>
                    <Route path="organizations">
                      <Route index element={<OrganizationsRegister />} />
                      <Route path="new" element={<OrganizationForm />} />
                      <Route path=":organizationId" element={<OrganizationDetail />} />
                      <Route path=":organizationId/edit" element={<OrganizationForm />} />
                    </Route>
                    <Route path="contacts">
                      <Route index element={<ContactsRegister />} />
                      <Route path="new" element={<ContactForm />} />
                      <Route path=":contactId" element={<ContactDetail />} />
                      <Route path=":contactId/edit" element={<ContactForm />} />
                    </Route>
                  </Route>
                  {/* Calendar & Tasks */}
                  <Route element={<RouteGroup name="Calendar & Tasks" />}>
                    <Route path="calendar">
                      <Route index element={<Calendar />} />
                      <Route path="new" element={<CalendarEventForm />} />
                      <Route path=":eventId" element={<CalendarEventForm />} />
                      <Route path=":eventId/edit" element={<CalendarEventForm />} />
                    </Route>
                    <Route path="tasks">
                      <Route index element={<Tasks />} />
                      <Route path="new" element={<TaskForm />} />
                      <Route path=":taskId" element={<TaskForm />} />
                      <Route path=":taskId/edit" element={<TaskForm />} />
                    </Route>
                    <Route path="vendors" element={<Vendors />} />
                  </Route>
                  {/* Contracts & Financial */}
                  <Route element={<RouteGroup name="Financial" />}>
                    <Route path="contracts">
                      <Route index element={<ContractRegister />} />
                      <Route path="new" element={<ContractForm />} />
                      <Route path=":contractId" element={<ContractDetail />} />
                      <Route path=":contractId/edit" element={<ContractForm />} />
                    </Route>
                    <Route path="payments" element={<Payments />} />
                    <Route path="invoices">
                      <Route index element={<Invoices />} />
                      <Route path="new" element={<InvoiceForm />} />
                      <Route path=":invoiceId" element={<InvoiceDetail />} />
                      <Route path=":invoiceId/edit" element={<InvoiceForm />} />
                    </Route>
                    <Route path="transactions">
                      <Route index element={<Transactions />} />
                      <Route path="new" element={<TransactionForm />} />
                      <Route path=":transactionId" element={<TransactionDetail />} />
                      <Route path=":transactionId/edit" element={<TransactionForm />} />
                    </Route>
                  </Route>
                  <Route path="search" element={<SearchResults />} />

                  {/* Activity Feed */}
                  <Route path="activities" element={<Activities />} />

                  {/* Reports */}
                  <Route element={<RouteGroup name="Reports" />}>
                    <Route path="reports">
                      <Route index element={<Reports />} />
                      <Route path="builder" element={<ReportBuilder />} />
                      <Route path="portfolio" element={<ClientPortfolioReport />} />
                      <Route path="player-package" element={<PlayerPackageBuilder />} />
                      <Route path="player-package/:clientId" element={<PlayerPackageBuilder />} />
                    </Route>
                  </Route>
                  {/* Communication. Team chat and the email inbox were removed:
                      an agency's own AI is already connected to those. What is
                      left is the agent side of athlete messaging, which nothing
                      outside this app can hold. */}
                  <Route element={<RouteGroup name="Communication" />}>
                    <Route path="client-inbox" element={<ClientInbox />} />
                  </Route>

                  <Route path="tags" element={<TagManager />} />
                  <Route path="goals" element={<Goals />} />
                  <Route path="relationships" element={<Relationships />} />
                  {/* Admin & Settings */}
                  <Route element={<RouteGroup name="Admin" />}>
                    <Route path="admin" element={<AdminPanel />} />
                    <Route path="admin/agency-setup" element={<AgencySetup />} />
                    <Route path="admin/data-health" element={<DataHealth />} />
                    <Route path="pending-customers" element={<PendingCustomers />} />
                    <Route path="settings" element={<Settings />} />
                    <Route path="team" element={<TeamManagement />} />
                  </Route>

                  <Route path="help" element={<Help />} />
                  <Route path="notifications" element={<NotificationCenter />} />
                </Route>
                </Route>
              </Route>

              {/* Client Portal routes */}
              <Route path="/portal/login" element={<PortalLogin />} />
              <Route path="/portal/start" element={<PortalStart />} />
              <Route path="/portal/verify" element={<PortalVerify />} />
              <Route path="/portal/verify/:token" element={<PortalVerify />} />
              <Route
                path="/portal"
                element={
                  <ClientSessionProvider>
                    <ClientProtectedRoute>
                      <RouteErrorBoundary groupName="Client Portal">
                        <ClientPortalLayout />
                      </RouteErrorBoundary>
                    </ClientProtectedRoute>
                  </ClientSessionProvider>
                }
              >
                <Route index element={<Navigate to="/portal/finances" replace />} />
                <Route path="finances" element={<PortalFinancialDashboard />} />
                <Route path="schedule" element={<PortalSchedule />} />
                <Route path="deals" element={<PortalDeals />} />
                <Route path="deals/:id" element={<PortalDealDetail />} />
                <Route path="contracts" element={<PortalContracts />} />
                <Route path="contracts/:id" element={<PortalContractDetail />} />
                <Route path="incentives" element={<PortalIncentives />} />
                <Route path="messages" element={<PortalMessages />} />
                <Route path="documents" element={<PortalDocuments />} />
                <Route path="actions" element={<PortalActionItems />} />
                <Route path="compliance" element={<PortalCompliance />} />
                <Route path="vendors" element={<PortalVendors />} />
                <Route path="profile" element={<PortalProfile />} />
              </Route>

              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
          <CookieConsent />
          </WorkViewProvider>
        </SessionProvider>
      </QueryProvider>
    </ErrorBoundary>
  )
}
